import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AxiosError } from "axios";
import { changePassword } from "../api/UserAPIUtils";
import IconPassword from "../components/Icons/IconPassword";
import LoadingSpinner from "../components/ui/feedback/LoadingSpinner";
import { passwordChanged } from "../features/auth/authSlice";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { useAppSelector } from "../hooks/useAppSelector";

const ChangePasswordWindow: React.FC = () => {
  const userId = useAppSelector((state) => state.user.id);
  const role = useAppSelector((state) => state.user.role?.toLocaleLowerCase());
  const [loading, setLoading] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Students use a platform-managed yearly password and cannot change it.
  if (role === "student") {
    return (
      <div className="flex h-screen items-center justify-center bg-[linear-gradient(180deg,#f8fbfb_0%,#f1f5f9_100%)] p-4">
        <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[30px] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfb_100%)] p-8 shadow-[0_24px_70px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/70 sm:h-auto sm:max-w-md lg:p-12">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_10px_24px_rgba(0,128,128,0.1)] ring-1 ring-primary/20">
            <IconPassword width="32px" height="32px" />
          </div>
          <h2 className="mb-3 text-[1.75rem] font-semibold tracking-[-0.03em] text-primary">
            Password Managed
          </h2>
          <p className="mb-8 max-w-sm text-center text-[15px] leading-relaxed text-slate-500">
            Your password is managed by the platform and does not need to be
            changed. Please contact your administrator if you have trouble
            logging in.
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="w-full rounded-2xl bg-primary px-4 py-3.5 text-[15px] font-semibold text-white shadow-[0_10px_24px_rgba(0,128,128,0.2)] transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (e.target.value !== confirmNewPassword) {
      setError("Passwords do not match");
    } else if (e.target.value.length < 8) {
      setError("Password must be at least 8 characters long");
    } else {
      setError("");
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmNewPassword(e.target.value);
    if (e.target.value !== newPassword) {
      setError("Passwords do not match");
    } else if (e.target.value.length < 8) {
      setError(
        "Password must be at least 8 characters, include 1 number, 1 special character, and 1 uppercase letter.",
      );
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    const passwordRegex =
      /^(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{}|;:,.<>?])[A-Za-z\d!@#$%^&*()_+\[\]{}|;:,.<>?]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setError(
        "Password must be at least 8 characters, include 1 number, 1 special character, and 1 uppercase letter.",
      );
      return;
    }
    // Call the reset password API
    if (userId === null) {
      setError("User ID is invalid");
      return;
    }
    setLoading(true);

    try {
      const response = await changePassword(oldPassword, newPassword, userId);
      if (response.success) {
        dispatch(passwordChanged());
        navigate("/");
      } else {
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "An unexpected error occurred.";
        setError(errorMessage);
      } else {
        setError("An unexpected error occurred.");
      }
      console.error("Failed to create new user", error);
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="flex h-screen items-center justify-center bg-[linear-gradient(180deg,#f8fbfb_0%,#f1f5f9_100%)] p-4">
        <div className="flex h-full w-full flex-col justify-center overflow-hidden rounded-[30px] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfb_100%)] p-8 shadow-[0_24px_70px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/70 sm:h-auto sm:max-w-md lg:p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_10px_24px_rgba(0,128,128,0.1)] ring-1 ring-primary/20">
              <IconPassword width="32px" height="32px" />
            </div>
            <h2 className="mb-3 text-[1.75rem] font-semibold tracking-[-0.03em] text-primary">
              Change Password
            </h2>
            <p className="mb-8 max-w-sm text-[15px] leading-relaxed text-slate-500">
              Must be at least 8 characters and include at least 1 number and 1
              special character.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Old Password
              </label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                className="w-full rounded-2xl border-0 bg-slate-50 px-4 py-3.5 text-[15px] text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 transition focus:bg-white focus:ring-2 focus:ring-inset focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={handlePasswordChange}
                required
                className="w-full rounded-2xl border-0 bg-slate-50 px-4 py-3.5 text-[15px] text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 transition focus:bg-white focus:ring-2 focus:ring-inset focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={handleConfirmPasswordChange}
                required
                className="w-full rounded-2xl border-0 bg-slate-50 px-4 py-3.5 text-[15px] text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 transition focus:bg-white focus:ring-2 focus:ring-inset focus:ring-primary"
              />
            </div>

            <div className="mt-4 flex flex-col gap-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-primary px-4 py-3.5 text-[15px] font-semibold text-white shadow-[0_10px_24px_rgba(0,128,128,0.2)] transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-70"
              >
                Update Password
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                disabled={loading}
                className="w-full rounded-2xl bg-slate-50 px-4 py-3.5 text-[15px] font-medium text-slate-700 ring-1 ring-inset ring-slate-200 transition hover:bg-slate-100 disabled:opacity-70"
              >
                Back to Dashboard
              </button>
            </div>

            {error && (
              <p className="mt-2 rounded-xl bg-red-50 p-3 text-center text-sm font-medium text-red-600 ring-1 ring-red-100">
                {error}
              </p>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordWindow;
